import { DashboardWidgetType, WidgetConstraints } from './dashboard-layout.model';

/**
 * Widget metadata for display in the edit toolbar
 */
export interface WidgetMetadata {
  type: DashboardWidgetType;
  displayName: string;
  icon: string;
  description?: string;
}

/**
 * Widget display metadata for the add widget menu
 */
export const WIDGET_METADATA: Record<DashboardWidgetType, WidgetMetadata> = {
  [DashboardWidgetType.CALENDAR]: { type: DashboardWidgetType.CALENDAR, displayName: 'Calendar', icon: 'fa-calendar' },
  [DashboardWidgetType.TASKS]: { type: DashboardWidgetType.TASKS, displayName: 'Tasks', icon: 'fa-tasks' },
  [DashboardWidgetType.EVICTIONS_BY_STEP]: { type: DashboardWidgetType.EVICTIONS_BY_STEP, displayName: 'Evictions by Status', icon: 'fa-chart-bar' },
  [DashboardWidgetType.EVICTION_BREAKDOWN]: { type: DashboardWidgetType.EVICTION_BREAKDOWN, displayName: 'Evictions Ratio', icon: 'fa-chart-pie' },
  [DashboardWidgetType.QUICK_LINKS]: { type: DashboardWidgetType.QUICK_LINKS, displayName: 'Quick Links', icon: 'fa-link' },
  [DashboardWidgetType.USAGE_SUMMARY]: { type: DashboardWidgetType.USAGE_SUMMARY, displayName: 'Usage Summary', icon: 'fa-chart-line' },
  [DashboardWidgetType.OPEN_EVICTIONS]: { type: DashboardWidgetType.OPEN_EVICTIONS, displayName: 'Open Evictions List', icon: 'fa-list' },
  [DashboardWidgetType.CLIENT_STATUS]: { type: DashboardWidgetType.CLIENT_STATUS, displayName: 'Client Status', icon: 'fa-building' },
  [DashboardWidgetType.MAP]: { type: DashboardWidgetType.MAP, displayName: 'State Map', icon: 'fa-map' },
  [DashboardWidgetType.PROPERTY_MAP]: { type: DashboardWidgetType.PROPERTY_MAP, displayName: 'Property Map', icon: 'fa-map-location-dot' },
  [DashboardWidgetType.CASES_CHART]: { type: DashboardWidgetType.CASES_CHART, displayName: 'Cases Chart', icon: 'fa-chart-area' },
  [DashboardWidgetType.ATTORNEY_BREAKDOWN]: { type: DashboardWidgetType.ATTORNEY_BREAKDOWN, displayName: 'Attorney Breakdown', icon: 'fa-users' },
  [DashboardWidgetType.RECENT_CASES]: { type: DashboardWidgetType.RECENT_CASES, displayName: 'Recent Cases', icon: 'fa-history' },
  [DashboardWidgetType.INTAKE_EVICTIONS]: { type: DashboardWidgetType.INTAKE_EVICTIONS, displayName: 'Evictions in Inbox', icon: 'fa-inbox' },
  [DashboardWidgetType.OPEN_EVICTIONS_COUNT]: { type: DashboardWidgetType.OPEN_EVICTIONS_COUNT, displayName: 'Open Evictions', icon: 'fa-gavel' },
  [DashboardWidgetType.UNREAD_MESSAGES]: { type: DashboardWidgetType.UNREAD_MESSAGES, displayName: 'Unread Messages', icon: 'fa-envelope' },
  [DashboardWidgetType.UNPAID_INVOICES]: { type: DashboardWidgetType.UNPAID_INVOICES, displayName: 'Unpaid Invoices', icon: 'fa-file-invoice-dollar' },
  [DashboardWidgetType.PENDING_CHECKS]: { type: DashboardWidgetType.PENDING_CHECKS, displayName: 'Pending Checks', icon: 'fa-money-check-alt' },
  [DashboardWidgetType.CASES_AWAITING_SUBMISSION]: { type: DashboardWidgetType.CASES_AWAITING_SUBMISSION, displayName: 'Cases Awaiting Submission', icon: 'fa-inbox' },
  [DashboardWidgetType.OUTSTANDING_BALANCE]: { type: DashboardWidgetType.OUTSTANDING_BALANCE, displayName: 'Total Outstanding Balance', icon: 'fa-file-invoice-dollar' }
};

/**
 * Widget size constraints for each dashboard widget type.
 * Defines minimum and maximum column/row dimensions for resizing.
 *
 * Requirements: 3.2, 3.3 (Widget Resizing constraints)
 * Requirements: 7.1-7.6 (Widget-Specific Size Constraints)
 *
 * Grid Configuration:
 * - Calendar: min 4x6, max 8x14 (Requirement 7.1)
 * - Tasks: min 4x3, max 12x8 (Requirement 7.2)
 * - Chart widgets: min 4x3, max varies (Requirement 7.3)
 * - Open Evictions: min 6x4, max 16x12 (Requirement 7.4)
 * - What's Next: min 4x3, max 16x8 (Requirement 7.5)
 * - Usage Summary: min 8x2, max 16x6 (Requirement 7.6)
 */
export const WIDGET_CONSTRAINTS: Record<DashboardWidgetType, WidgetConstraints> = {
  [DashboardWidgetType.CALENDAR]: { minCols: 4, minRows: 6, maxCols: 8, maxRows: 14 },
  [DashboardWidgetType.TASKS]: { minCols: 4, minRows: 3, maxCols: 12, maxRows: 8 },
  [DashboardWidgetType.EVICTIONS_BY_STEP]: { minCols: 4, minRows: 3, maxCols: 12, maxRows: 8 },
  [DashboardWidgetType.EVICTION_BREAKDOWN]: { minCols: 4, minRows: 3, maxCols: 8, maxRows: 8 },
  [DashboardWidgetType.QUICK_LINKS]: { minCols: 4, minRows: 3, maxCols: 16, maxRows: 8 },
  [DashboardWidgetType.USAGE_SUMMARY]: { minCols: 8, minRows: 2, maxCols: 16, maxRows: 6 },
  [DashboardWidgetType.OPEN_EVICTIONS]: { minCols: 6, minRows: 4, maxCols: 16, maxRows: 12 },
  [DashboardWidgetType.CLIENT_STATUS]: { minCols: 4, minRows: 1, maxCols: 8, maxRows: 2 },
  [DashboardWidgetType.MAP]: { minCols: 6, minRows: 6, maxCols: 16, maxRows: 10 },
  [DashboardWidgetType.PROPERTY_MAP]: { minCols: 6, minRows: 6, maxCols: 16, maxRows: 10 },
  [DashboardWidgetType.CASES_CHART]: { minCols: 8, minRows: 2, maxCols: 20, maxRows: 6 },
  [DashboardWidgetType.ATTORNEY_BREAKDOWN]: { minCols: 4, minRows: 3, maxCols: 8, maxRows: 6 },
  [DashboardWidgetType.RECENT_CASES]: { minCols: 3, minRows: 3, maxCols: 8, maxRows: 8 },
  // Summary card widgets - fixed size (1 row height)
  [DashboardWidgetType.INTAKE_EVICTIONS]: { minCols: 3, minRows: 1, maxCols: 6, maxRows: 1 },
  [DashboardWidgetType.OPEN_EVICTIONS_COUNT]: { minCols: 3, minRows: 1, maxCols: 6, maxRows: 1 },
  [DashboardWidgetType.UNREAD_MESSAGES]: { minCols: 3, minRows: 1, maxCols: 6, maxRows: 1 },
  [DashboardWidgetType.UNPAID_INVOICES]: { minCols: 3, minRows: 1, maxCols: 6, maxRows: 1 },
  [DashboardWidgetType.PENDING_CHECKS]: { minCols: 3, minRows: 1, maxCols: 6, maxRows: 1 },
  [DashboardWidgetType.CASES_AWAITING_SUBMISSION]: { minCols: 4, minRows: 1, maxCols: 8, maxRows: 2 },
  [DashboardWidgetType.OUTSTANDING_BALANCE]: { minCols: 4, minRows: 1, maxCols: 8, maxRows: 2 }
};
